<template>
  <div>
    <b-modal
        v-model="showModalInternal"
        id="visual-reports-form-modal"
        scrollable
        :title="title"
        title-class="font-18"
        size="lg"
        hide-footer
    >

      <!-- Step 1: Report type selection (create mode only) -->
      <div v-if="!isEditMode && !typeSelected">
        <div class="card shadow-sm mb-4">
          <div class="card-header bg-light">
            <h6 class="mb-0 text-primary fw-bold">
              <i class="fas fa-list me-2"></i>Wybierz typ raportu wizualnego
            </h6>
          </div>
          <div class="card-body">
            <div class="row g-3">
              <div class="col-12">
                <label class="form-label required">Typ raportu</label>
                <div v-if="loadingTypes" class="text-center py-3">
                  <i class="fas fa-spinner fa-spin text-primary"></i>
                  <span class="ms-2 text-muted">Pobieranie typów raportów...</span>
                </div>
                <select
                    v-else
                    class="form-select"
                    v-model="formData.reportType"
                    :class="{'is-invalid': typeValidationError}"
                >
                  <option value="">-- Wybierz typ raportu --</option>
                  <option
                      v-for="reportType in availableReportTypes"
                      :key="reportType"
                      :value="reportType"
                  >
                    {{ reportType }}
                  </option>
                </select>
                <div v-if="typeValidationError" class="invalid-feedback">Wybór typu raportu jest wymagany</div>
              </div>
            </div>
          </div>
          <div class="card-footer d-flex justify-content-end gap-2">
            <button type="button" class="btn btn-secondary" @click="cancel">
              <i class="fas fa-times me-1"></i>Anuluj
            </button>
            <button type="button" class="btn btn-primary" @click="proceedToForm" :disabled="!formData.reportType">
              <i class="fas fa-arrow-right me-1"></i>Dalej
            </button>
          </div>
        </div>
      </div>

      <!-- Step 2: Full form -->
      <div v-if="isEditMode || typeSelected">

        <div v-if="loadingForm" class="text-center py-5">
          <i class="fas fa-spinner fa-spin fa-2x text-primary"></i>
          <p class="mt-2 text-muted">Ładowanie danych formularza...</p>
        </div>

        <div v-else>
          <div class="card shadow-sm mb-4">
            <div class="card-header bg-light">
              <h6 class="mb-0 text-primary fw-bold">
                <i class="fas fa-info-circle me-2"></i>Informacje podstawowe
              </h6>
            </div>
            <div class="card-body">
              <div class="row g-3">

                <div class="col-md-6">
                  <label class="form-label required">Typ raportu</label>
                  <input
                      type="text"
                      class="form-control"
                      :value="formData.reportType"
                      readonly
                      disabled
                  />
                </div>

                <div class="col-md-6">
                  <label class="form-label required">Nazwa raportu</label>
                  <input
                      type="text"
                      :class="['form-control', nameInputClass]"
                      v-model="formData.name"
                      placeholder="Wprowadź nazwę raportu"
                      @blur="validateNameOnBlur"
                  />
                  <div class="invalid-feedback">{{ nameErrorMessage }}</div>
                </div>

                <div class="col-12">
                  <label class="form-label">Opis</label>
                  <textarea
                      class="form-control"
                      rows="2"
                      v-model="formData.description"
                      placeholder="Opis raportu"
                  ></textarea>
                </div>

                <div class="col-md-6">
                  <label class="form-label">Autor</label>
                  <input
                      type="text"
                      class="form-control"
                      v-model="formData.creator"
                      placeholder="Imię i nazwisko autora"
                  />
                </div>

              </div>
            </div>
          </div>

          <!-- Validation errors -->
          <div v-if="validationErrors.length > 0" class="alert alert-danger py-2">
            <ul class="mb-0 ps-3">
              <li v-for="(err, idx) in validationErrors" :key="idx">{{ err }}</li>
            </ul>
          </div>

          <!-- Footer actions -->
          <div class="modal-footer-container">
            <div class="modal-footer-bottom">
              <button type="button" class="btn btn-secondary" @click="cancel" :disabled="saving">
                <i class="fas fa-times me-1"></i>Anuluj
              </button>
              <button type="button" class="btn btn-primary" @click="save" :disabled="saving">
                <span v-if="saving">
                  <i class="fas fa-spinner fa-spin me-1"></i>Zapisywanie...
                </span>
                <span v-else>
                  <i class="fas fa-save me-1"></i>Zapisz
                </span>
              </button>
            </div>
          </div>

        </div>
      </div>

    </b-modal>
  </div>
</template>

<script>
import {BModal} from "bootstrap-vue-next";
import {log} from "@/state/pinia/log";
import {useVisualReportsModule} from "@/state/pinia/visualReportsModule";
import {useLazyLoaderModule} from "@/state/pinia/lazyloaderManagerModule";

export default {
  name: "VisualReportsFormModal",
  components: {
    BModal,
  },
  props: {
    showModal: {
      type: Boolean,
      required: true,
    },
    title: {
      type: String,
      default: "Raport wizualny",
    },
    reportGuid: {
      type: String,
      default: null,
    },
  },
  emits: ["update:showModal", "onSave", "onClose", "onCreated"],
  data() {
    return {
      showModalInternal: this.showModal,
      isEditMode: false,
      typeSelected: false,

      loadingTypes: false,
      loadingForm: false,
      saving: false,

      typeValidationError: false,
      nameValidationState: null,
      nameErrorMessage: "",
      validationErrors: [],

      availableReportTypes: [],

      formData: {
        reportType: "",
        name: "",
        description: "",
        creator: "",
      },
    };
  },
  computed: {
    visualReportsModule() {
      return useVisualReportsModule();
    },
    lazyloaderModule() {
      return useLazyLoaderModule();
    },
    nameInputClass() {
      if (this.nameValidationState === null) return "";
      return this.nameValidationState ? "is-valid" : "is-invalid";
    },
  },
  methods: {
    init() {
      if (this.reportGuid) {
        this.isEditMode = true;
        this.typeSelected = false;
        this.loadReportForEdit();
      } else {
        this.isEditMode = false;
        this.typeSelected = false;
        this.loadAvailableReportTypes();
      }
    },

    loadAvailableReportTypes() {
      this.loadingTypes = true;
      try {
        this.visualReportsModule.httpGetVisualReportTypes().then((response) => {
          this.availableReportTypes = this.visualReportsModule.getAvailableVisualReportTypes;
        })

      } finally {
        this.loadingTypes = false;
      }
    },

    proceedToForm() {
      if (!this.formData.reportType) {
        this.typeValidationError = true;
        return;
      }
      this.typeValidationError = false;
      this.typeSelected = true;
    },

    loadReportForEdit() {
      this.loadingForm = true;
      this.lazyloaderModule.setDisplayLazyLoader(true);
      this.lazyloaderModule.setProgressBarMessage("Pobieranie danych raportu...");
      try {
        this.visualReportsModule.httpGetVisualReport(this.reportGuid).then((response) => {
          log.log("Loaded report data:", response);
          if (response && response.status === 200) {
            const data = response.data.payload;
            this.formData = {
              reportType: data.reportType || "",
              name: data.name || "",
              description: data.description || "",
              creator: data.creator || "",
            };
          }

        })

      } finally {
        this.loadingForm = false;
        this.lazyloaderModule.setDisplayLazyLoader(false);
        this.lazyloaderModule.setProgressBarMessage("");
      }
    },

    validateNameOnBlur() {
      if (!this.formData.name || this.formData.name.trim() === "") {
        this.nameValidationState = false;
        this.nameErrorMessage = "Nazwa raportu jest wymagana";
      } else {
        this.nameValidationState = true;
        this.nameErrorMessage = "";
      }
    },

    validateLocalForm() {
      this.validationErrors = [];
      if (!this.formData.name || this.formData.name.trim() === "") {
        this.validationErrors.push("Nazwa raportu jest wymagana");
        this.nameValidationState = false;
        this.nameErrorMessage = "Nazwa raportu jest wymagana";
        return false;
      }
      return true;
    },

    save() {
      if (!this.validateLocalForm()) return;

      this.saving = true;
      this.lazyloaderModule.setDisplayLazyLoader(true);
      this.lazyloaderModule.setProgressBarMessage("Zapisywanie raportu...");

      try {
        if (this.isEditMode && this.reportGuid) {
          this.visualReportsModule.httpUpdateVisualReport(this.reportGuid, {
            name: this.formData.name,
            description: this.formData.description,
          }).then((response) => {
            if (response && response.status === 200) {
              this.$emit("onSave");
              this.closeModal();
            }
          })

        } else {
          const createPayload = {
            reportType: this.formData.reportType,
            name: this.formData.name,
            description: this.formData.description,
          };
          if (this.formData.creator && this.formData.creator.trim() !== "") {
            createPayload.creator = this.formData.creator;
          }
          this.visualReportsModule.httpCreateVisualReport(createPayload).then((response) => {
            if (response && response.status === 200) {
              const created = response.data.payload;
              log.log("Created visual report:", created);
              this.$emit("onCreated", created);
              this.$emit("onSave");
              this.closeModal();
            }
          });

        }
      } finally {
        this.saving = false;
        this.lazyloaderModule.setDisplayLazyLoader(false);
        this.lazyloaderModule.setProgressBarMessage("");
      }
    },

    cancel() {
      this.closeModal();
    },
    closeModal() {
      this.showModalInternal = false;
      this.$emit("onClose");
    },
  },
  watch: {
    showModal(newValue) {
      this.showModalInternal = newValue;
    },
    showModalInternal(newValue) {
      this.$emit("update:showModal", newValue);
    },
  },
  created() {
    log.log("VisualReportsFormModal created, reportGuid:", this.reportGuid);
    this.init();
  },
};
</script>

<style scoped>
.modal-footer-container {
  position: sticky;
  bottom: -19px;
  left: 0;
  width: 100%;
  background: #fff;
  z-index: 60;
  padding: 0;
  border-top: 1px solid #dee2e6;
  margin-top: 16px;
}

.modal-footer-bottom {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 14px 0 4px 0;
}

.required::after {
  content: " *";
  color: #f46a6a;
}
</style>
